<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { ChevronLeft, ChevronRight, RotateCw, ZoomIn, ZoomOut } from '@lucide/vue'
import RrModal from '../modal/RrModal.vue'
import RrButton from '../button/RrButton.vue'
import type { PreviewImage, ImagePreviewOptions } from './types'

const props = defineProps<{ open: boolean; images: PreviewImage[]; current: number; getContainer?: ImagePreviewOptions['getContainer'] }>()
const emit = defineEmits<{ 'update:open': [open: boolean]; 'update:current': [current: number] }>()
const scale = ref(1)
const rotation = ref(0)
const failed = ref(false)
const current = computed(() => Math.max(0, Math.min(props.current, props.images.length - 1)))
const image = computed(() => props.images[current.value])
watch([() => props.open, image], () => { scale.value = 1; rotation.value = 0; failed.value = false })
function previous() { if (current.value > 0) emit('update:current', current.value - 1) }
function next() { if (current.value < props.images.length - 1) emit('update:current', current.value + 1) }
function keydown(event: KeyboardEvent) {
  if (event.key === 'ArrowLeft') { event.preventDefault(); previous() }
  else if (event.key === 'ArrowRight') { event.preventDefault(); next() }
}
</script>

<template>
  <RrModal :open="open && images.length > 0" :title="image?.alt || '图片预览'" :width="1040" centered :footer="null" :destroy-on-close="true" :get-container="getContainer" @update:open="emit('update:open', $event)">
    <div class="rr-image-preview" @keydown="keydown">
      <div class="rr-image-preview__toolbar"><span aria-live="polite">{{ current + 1 }} / {{ images.length }}</span><RrButton type="text" size="small" :disabled="scale <= 0.5" aria-label="缩小" @click="scale = Math.max(0.5, scale - 0.25)"><ZoomOut :size="16" /></RrButton><RrButton type="text" size="small" :disabled="scale >= 4" aria-label="放大" @click="scale = Math.min(4, scale + 0.25)"><ZoomIn :size="16" /></RrButton><RrButton type="text" size="small" aria-label="旋转" @click="rotation += 90"><RotateCw :size="16" /></RrButton></div>
      <div class="rr-image-preview__viewport" tabindex="0" aria-label="图片，左右方向键切换" data-autofocus>
        <p v-if="failed" role="alert">图片加载失败</p><img v-else-if="image" :src="image.src" :alt="image.alt" :style="{ transform: `scale(${scale}) rotate(${rotation}deg)` }" @error="failed = true" />
      </div>
      <div v-if="images.length > 1" class="rr-image-preview__navigation"><RrButton type="text" :disabled="current === 0" aria-label="上一张" @click="previous"><ChevronLeft :size="16" />上一张</RrButton><RrButton type="text" :disabled="current === images.length - 1" aria-label="下一张" @click="next">下一张<ChevronRight :size="16" /></RrButton></div>
    </div>
  </RrModal>
</template>

<style scoped>
.rr-image-preview { min-width: 0; }
.rr-image-preview__toolbar { display: flex; align-items: center; justify-content: flex-end; gap: var(--rr-space-2); padding-bottom: var(--rr-space-2); }
.rr-image-preview__viewport { display: grid; place-items: center; width: 100%; height: min(62vh, 720px); overflow: auto; background: var(--rr-color-surface-soft); border-radius: var(--rr-radius-control); }
.rr-image-preview__viewport img { display: block; max-width: 100%; max-height: 100%; object-fit: contain; transition: transform 160ms ease; }
.rr-image-preview__navigation { display: flex; justify-content: space-between; padding-top: var(--rr-space-2); }
@media (prefers-reduced-motion: reduce) { .rr-image-preview__viewport img { transition: none; } }
</style>
